import Image from "next/image";
import { z } from "zod";

import { Icons } from "@/components/icons/outline-icons";
import { RichText } from "@/components/rich-text";
import { cn } from "@/lib/utils";
import { customCardSchema } from "@/schemas";

import { AnimationPlayer } from "../animation-player";
import { Link } from "../ui/link";
import { VideoPlayer } from "./shared/video-player";

export function WithLink({
  className,
  ...props
}: z.infer<typeof customCardSchema> & { className?: string }) {
  const {
    title,
    richTextTitle,
    label,
    image,
    video,
    mediaType,
    bgColor,
    url,
    external,
    videoAnimation,
    animation,
    loopAnimation,
  } = props;

  const backgroundColor = bgColor?.value ?? "#FAF9F8";

  return (
    <Link
      href={url ?? "/"}
      external={!!external}
      target={external ? "_blank" : undefined}
      data-ph-capture-attribute-cta_label={`Custom Card (WithLink) - ${title}`}
      data-ph-capture-attribute-cta_url={url}
    >
      <article
        className={cn(
          "group rounded-container mx-auto flex w-full flex-col overflow-hidden md:max-w-[560px]",
          className
        )}
      >
        <div
          className="flex h-[288px] w-full items-center justify-center overflow-hidden lg:h-[440px]"
          style={{ backgroundColor }}
        >
          {image?.src && mediaType === "image" && (
            <Image
              className="size-full overflow-hidden object-cover"
              src={image.src}
              alt={image.alt ?? "Large Card Media"}
              width={image.width ?? 0}
              height={image.height ?? 0}
              sizes="(min-width: 624px) 50vw, 100vw"
            />
          )}
          {video && mediaType === "video" && video?.asset && (
            <VideoPlayer
              className="size-full"
              asset={video.asset}
              animation={false}
            />
          )}
          {mediaType === "videoAnimation" && videoAnimation?.asset && (
            <VideoPlayer
              className="h-full w-auto overflow-hidden object-contain"
              asset={videoAnimation.asset}
              animation={true}
            />
          )}
          {animation && mediaType === "animation" && (
            <AnimationPlayer
              bgColor={backgroundColor}
              animation={animation}
              loop={loopAnimation}
            />
          )}
        </div>
        <div className="rounded-b-container border-grey-100 flex flex-col justify-between gap-6 border border-t-0 p-4 pb-5 lg:p-6 lg:pb-8">
          <div className="flex flex-col gap-1">
            <span className="text-label-10 lg:text-label-12 text-black uppercase">
              {label}
            </span>
            {richTextTitle ? (
              <div className="text-heading-24 text-black">
                <RichText value={richTextTitle} />
              </div>
            ) : (
              <span className="text-heading-24 text-black">{title}</span>
            )}
          </div>
          <div className="flex transition-transform duration-200 group-hover:translate-x-2.5">
            <Icons.ArrowRight className="size-6" />
          </div>
        </div>
      </article>
    </Link>
  );
}
